// src/components/Prompt.js
import React, { useEffect, useState } from 'react';
import { fetchAllPrompts, deletePrompt } from '../api/promptApi';
import {
    Container,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    Button,
} from '@mui/material';
import PromptCard from './PromptCard';
import ErrorBoundary from './ErrorBoundary';
import AddPromptModal from './AddPromptModal';

const Prompt = () => {
    const [promptData, setPromptData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Fetch data from the API
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchAllPrompts();
            // Sort the data by created_at in descending order (most recent first)
            const sortedData = data.sort((a, b) => 
                new Date(b.created_at) - new Date(a.created_at)
            );
            setPromptData(sortedData);
        } catch (err) {
            console.error('Error fetching prompts:', err);
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    // Delete a specific prompt
    const handleDelete = async (id) => {
        try {
            await deletePrompt(id);
            setPromptData((prevData) => prevData.filter((item) => item.id !== id));
        } catch (err) {
            console.error(`Failed to delete prompt with ID ${id}:`, err);
            setError(`Failed to delete prompt with ID ${id}`);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchData();
    }, []);

    // Modal open/close handlers
    const handleModalOpen = () => setIsModalOpen(true);
    const handleModalClose = () => setIsModalOpen(false);

    // Refresh data after successful addition
    const handleSuccess = () => fetchData();

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Typography variant="h4" align="center" gutterBottom>
                Prompt Base
            </Typography>

            <Button
                variant="contained"
                color="primary"
                onClick={handleModalOpen}
                sx={{ mb: 2 }}
            >
                Add Prompt
            </Button>

            {loading && (
                <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />
            )}
            {error && <Alert severity="error">{error}</Alert>}

            <Grid container spacing={3}>
                {promptData.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <ErrorBoundary>
                            <PromptCard
                                id={item.id}
                                prompt={item.prompt}
                                created_at={item.created_at}
                                user_id={item.user_id}
                                user_email={item.user_email}
                                metadata={item.metadata}
                                handleDelete={handleDelete}
                            />
                        </ErrorBoundary>
                    </Grid>
                ))}
            </Grid>

            <AddPromptModal
                open={isModalOpen}
                onClose={handleModalClose}
                onSuccess={handleSuccess}
            />
        </Container>
    );
};

export default Prompt;
